import { useEffect, useState } from "react";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { ModelPicker } from "../components/ModelPicker";

type Provider = "anthropic" | "openai" | "azure_openai" | "webhook";

interface AiConfig {
  aiProvider: Provider | null;
  aiKeyVaultUri: string | null;
  aiEndpointUrl: string | null;
  aiModel: string | null;
  hasWebhookSecret: boolean;
  aiSystemPrompt: string | null;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  anthropic:    "Anthropic (via your Key Vault)",
  openai:       "OpenAI (via your Key Vault)",
  azure_openai: "Azure OpenAI (via your Key Vault)",
  webhook:      "Custom webhook",
};

const PROVIDER_DESC: Record<Provider, string> = {
  anthropic:    "Use your own Anthropic API key stored in Azure Key Vault. Grant the platform's managed identity Get access on the secret.",
  openai:       "Use your own OpenAI API key stored in Azure Key Vault. Grant the platform's managed identity Get access on the secret.",
  azure_openai: "Use your own Azure OpenAI deployment. Store your api-key in Azure Key Vault and provide your deployment base URL.",
  webhook:      "Your own HTTPS endpoint receives the compliance prompt and returns the structured result. The key never leaves your infrastructure.",
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: theme.color.subtle,
  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};

export function ConsultantAiSettingsPage() {
  const [config,   setConfig]   = useState<AiConfig | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState("");

  // Local form state
  const [provider,     setProvider]     = useState<Provider | "platform">("platform");
  const [kvUri,        setKvUri]        = useState("");
  const [endpointUrl,  setEndpointUrl]  = useState("");
  const [model,        setModel]        = useState("");
  const [webhookUrl,   setWebhookUrl]   = useState("");
  const [hasSecret,    setHasSecret]    = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [generatingSecret, setGeneratingSecret] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");

  useEffect(() => {
    api.get<AiConfig>("/consultant/ai-config")
      .then((d) => {
        setConfig(d);
        setProvider(d.aiProvider ?? "platform");
        setKvUri(d.aiKeyVaultUri ?? "");
        setEndpointUrl(d.aiEndpointUrl ?? "");
        setModel(d.aiModel ?? "");
        setWebhookUrl(d.aiEndpointUrl ?? "");
        setHasSecret(d.hasWebhookSecret);
        setSystemPrompt(d.aiSystemPrompt ?? "");
      })
      .catch(() => setError("Failed to load AI configuration"))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true); setSaved(false); setError("");
    try {
      const isWebhook = provider === "webhook";
      const isKv      = provider !== "platform" && !isWebhook;
      await api.patch("/consultant/ai-config", {
        aiProvider:    provider === "platform" ? null : provider,
        aiKeyVaultUri: isKv      ? (kvUri      || null) : null,
        aiEndpointUrl: isWebhook ? (webhookUrl || null) : (provider === "azure_openai" ? (endpointUrl || null) : null),
        aiModel:       model || null,
        aiSystemPrompt: systemPrompt.trim() || null,
      });
      setSaved(true);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(err, err?.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleGenerateSecret() {
    setGeneratingSecret(true); setError("");
    try {
      const res = await api.patch<{ ok: boolean; webhookSecret?: string }>("/consultant/ai-config", {
        aiProvider:              "webhook",
        aiEndpointUrl:           webhookUrl || null,
        regenerateWebhookSecret: true,
      });
      if (res.webhookSecret) {
        setRevealedSecret(res.webhookSecret);
        setHasSecret(true);
      }
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(err, err?.message || "Failed to generate secret"));
    } finally {
      setGeneratingSecret(false);
    }
  }

  if (loading) return <LoadingBlock />;

  const activeProvider = provider === "platform" ? null : provider as Provider;
  const usesKv = activeProvider && activeProvider !== "webhook";

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>AI Provider</h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
        By default compliance checks run on the platform's AI, on the model Abacas chooses (Claude Sonnet 5.5).
        Connect your own key to choose the provider and the model yourself — your API key is fetched directly
        from your Azure Key Vault; Abacas stores only the secret URI. Your choice applies to every client using
        your consultancy's AI; a client can instead use the platform AI or their own key and model.
      </p>

      {/* Provider selector */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>Provider</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

          <ProviderOption
            value="platform"
            label="Platform default (Abacas Anthropic)"
            desc="Use the platform's shared Anthropic account, on the model Abacas chooses. No configuration needed."
            selected={provider === "platform"}
            onSelect={() => setProvider("platform")}
          />

          {(["anthropic", "openai", "azure_openai", "webhook"] as const).map((p) => (
            <ProviderOption
              key={p}
              value={p}
              label={PROVIDER_LABELS[p]}
              desc={PROVIDER_DESC[p]}
              selected={provider === p}
              onSelect={() => { if (p !== provider) setModel(""); setProvider(p); setRevealedSecret(null); }}
            />
          ))}
        </div>
      </div>

      {/* Key Vault config */}
      {usesKv && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
            Azure Key Vault secret URI
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
            Grant the platform managed identity <code style={{ background: theme.color.panelAlt, padding: "1px 5px", borderRadius: 4 }}>Get</code> access
            on this secret in your Key Vault. Abacas stores only this URI — never the key value.
          </div>
          <label style={labelStyle}>Secret URI</label>
          <input
            value={kvUri}
            onChange={(e) => setKvUri(e.target.value)}
            placeholder="https://your-vault.vault.azure.net/secrets/ai-api-key"
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12 }}
          />

          {activeProvider === "azure_openai" && (
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Azure OpenAI deployment base URL</label>
              <input
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                placeholder="https://your-resource.openai.azure.com"
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12 }}
              />
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>{activeProvider === "azure_openai" ? "Deployment" : "Model"}</label>
            <ModelPicker
              key={activeProvider}
              provider={activeProvider as "anthropic" | "openai" | "azure_openai"}
              value={model || null}
              onChange={(m) => setModel(m ?? "")}
            />
          </div>
        </div>
      )}

      {/* Webhook config */}
      {activeProvider === "webhook" && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
            Webhook configuration
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
            Your endpoint will receive a POST with the compliance prompt. Respond with
            {" "}<code style={{ background: theme.color.panelAlt, padding: "1px 5px", borderRadius: 4 }}>{"{ \"result\": \"<JSON string>\" }"}</code>.
            Each request is signed with <code style={{ background: theme.color.panelAlt, padding: "1px 5px", borderRadius: 4 }}>X-Abacas-Signature: sha256=…</code>.
          </div>

          <label style={labelStyle}>Callback URL</label>
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://ai.yourfirm.com/compliance-check"
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12, marginBottom: 16 }}
          />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hasSecret ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>HMAC signing secret</div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                {hasSecret ? "A secret is configured. Regenerate only if you need to rotate it." : "Generate a secret to enable request signing."}
              </div>
            </div>
            <button
              onClick={handleGenerateSecret}
              disabled={generatingSecret}
              style={{ ...btnPrimary, fontSize: 12, padding: "7px 14px", opacity: generatingSecret ? 0.7 : 1, whiteSpace: "nowrap" }}
            >
              {generatingSecret ? "Generating…" : hasSecret ? "Regenerate secret" : "Generate secret"}
            </button>
          </div>

          {revealedSecret && (
            <div style={{ marginTop: 4, background: theme.color.successBg, border: `1px solid ${theme.color.success}`, borderRadius: 3, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.successText, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Copy this secret now — it will not be shown again
              </div>
              <code style={{ fontSize: 12, color: theme.color.text, wordBreak: "break-all", lineHeight: 1.6, display: "block" }}>
                {revealedSecret}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(revealedSecret)}
                style={{ marginTop: 10, fontSize: 11, padding: "4px 12px", borderRadius: 3, background: "transparent", border: `1px solid ${theme.color.success}`, color: theme.color.successText, cursor: "pointer" }}
              >
                Copy to clipboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* How it works — Key Vault setup guidance */}
      {usesKv && (
        <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Setup: granting access</div>
          <ol style={{ fontSize: 12, color: theme.color.subtle, paddingLeft: 18, lineHeight: 2, margin: 0 }}>
            <li>In your Azure Key Vault, go to <strong>Access policies</strong></li>
            <li>Add the platform's BYOK managed identity (<code style={{ background: theme.color.panelAlt, padding: "1px 4px", borderRadius: 3 }}>iso27001comply-prod-byok-id</code>) with <strong>Get</strong> secret permission only</li>
            <li>Store your API key as a secret and copy its URI (including the version if you want to pin it)</li>
            <li>Paste the URI above and save</li>
          </ol>
        </div>
      )}

      {/* System prompt */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
          Firm system prompt
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.6 }}>
          These instructions are prepended to every compliance check as the AI system prompt.
          Use them to describe your firm's methodology, preferred terminology, or reporting style.
          They sit on top of the platform's compliance rules — the assessment scoring criteria always apply.
          Leave blank to use the platform default behaviour.
        </div>
        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder={`e.g. You are a compliance consultant at Acme Advisory. When identifying gaps, always reference the relevant clause of the standard by number. Prioritise practical, actionable recommendations suitable for an organisation in the financial services sector. Where a control is borderline, apply professional scepticism and default to PARTIAL rather than COMPLIANT.`}
          rows={7}
          maxLength={8000}
          style={{
            ...inputStyle,
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical",
            fontFamily: "inherit",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        />
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6, textAlign: "right" }}>
          {systemPrompt.length} / 8000 characters
        </div>
      </div>

      {/* Save */}
      {provider !== "webhook" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
          {saved  && <span style={{ fontSize: 12, color: theme.color.successText }}>Saved</span>}
          {error  && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
        </div>
      )}
      {provider === "webhook" && error && (
        <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>
      )}
    </div>
  );
}

function ProviderOption({
  value, label, desc, selected, onSelect,
}: {
  value: string; label: string; desc: string; selected: boolean; onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      style={{
        textAlign: "left", padding: "10px 14px", borderRadius: 3, cursor: "pointer",
        border: `1.5px solid ${selected ? theme.color.accent : theme.color.border}`,
        background: selected ? theme.color.accentBg : theme.color.panelAlt,
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <div style={{
          width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
          border: `2px solid ${selected ? theme.color.accent : theme.color.border}`,
          background: selected ? theme.color.accent : "transparent",
        }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{label}</div>
      </div>
      <div style={{ fontSize: 11, color: theme.color.subtle, paddingLeft: 22, lineHeight: 1.5 }}>{desc}</div>
    </button>
  );
}
